import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Loader2,
  History,
  AlertCircle,
  User,
  Shield,
  Clock,
  RefreshCw,
  Building2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminCampaignHistoryPage() {
  const navigate = useNavigate();
  const { campaignId } = useParams();
  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);

  useEffect(() => {
    fetchData();
  }, [campaignId]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('dme_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [campaignRes, auditRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/campaigns/${campaignId}`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/admin/campaigns/${campaignId}/audit`, { headers: getAuthHeaders() })
      ]);
      setCampaign(campaignRes.data);
      setAuditLogs(auditRes.data);
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('Campaign not found');
        navigate('/admin/campaigns');
      } else {
        toast.error('Failed to load history');
      }
    } finally {
      setLoading(false);
    }
  };

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'created': return 'bg-green-100 text-green-800';
      case 'updated': return 'bg-blue-100 text-blue-800';
      case 'toggled': return 'bg-orange-100 text-orange-800';
      case 'state_change': return 'bg-purple-100 text-purple-800';
      case 'admin_override': return 'bg-red-100 text-red-800';
      case 'deleted': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-campaign-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/campaigns')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <History className="w-6 h-6" />
              Campaign Audit History
            </h1>
            <p className="text-muted-foreground">{campaign?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={() => navigate(`/admin/campaigns/${campaignId}/edit`)}>
            Edit Campaign
          </Button>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Campaign Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            Campaign Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-5 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Client:</span>
              <p className="font-medium">{campaign?.client_name}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Campaign:</span>
              <p className="font-medium">{campaign?.name}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Status:</span>
              <p><Badge variant={campaign?.is_active ? 'default' : 'secondary'}>{campaign?.is_active ? 'Active' : 'Paused'}</Badge></p>
            </div>
            <div>
              <span className="text-muted-foreground">Created:</span>
              <p className="font-medium">{formatDate(campaign?.created_at)}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Updated:</span>
              <p className="font-medium">{formatDate(campaign?.updated_at)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Audit Log Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Full Audit Trail ({auditLogs.length} records)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {auditLogs.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No audit records found</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[180px]">Timestamp</TableHead>
                  <TableHead className="w-[120px]">Action</TableHead>
                  <TableHead className="w-[200px]">Changed By</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.map((log) => (
                  <TableRow key={log.id} className={log.action === 'admin_override' ? 'bg-orange-50' : ''}>
                    <TableCell className="font-mono text-sm">
                      {formatDate(log.timestamp)}
                    </TableCell>
                    <TableCell>
                      <Badge className={getActionBadgeColor(log.action)}>
                        {log.action.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {log.changed_by?.type === 'admin' ? (
                          <Shield className="w-4 h-4 text-orange-500" />
                        ) : (
                          <User className="w-4 h-4 text-blue-500" />
                        )}
                        <div>
                          <p className="font-medium text-sm">{log.changed_by?.name || log.changed_by?.email || 'Unknown'}</p>
                          <p className="text-xs text-muted-foreground">{log.changed_by?.type}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm space-y-1">
                        {log.action === 'toggled' && (
                          <p>Campaign turned <strong>{log.details?.toggled_to}</strong></p>
                        )}
                        {log.action === 'state_change' && (
                          <div>
                            <p>States updated: <strong>{log.details?.total_states}</strong> selected</p>
                            {log.details?.states_added?.length > 0 && (
                              <p className="text-green-600 text-xs">+ Added: {log.details.states_added.slice(0, 5).join(', ')}{log.details.states_added.length > 5 ? ` +${log.details.states_added.length - 5} more` : ''}</p>
                            )}
                            {log.details?.states_removed?.length > 0 && (
                              <p className="text-red-600 text-xs">- Removed: {log.details.states_removed.slice(0, 5).join(', ')}{log.details.states_removed.length > 5 ? ` +${log.details.states_removed.length - 5} more` : ''}</p>
                            )}
                          </div>
                        )}
                        {log.action === 'admin_override' && (
                          <div className="flex items-center gap-2 text-orange-600">
                            <AlertCircle className="w-4 h-4" />
                            <span>Admin Override - Fields: {log.details?.fields_changed?.join(', ')}</span>
                          </div>
                        )}
                        {log.action === 'created' && <p>Campaign created</p>}
                        {log.action === 'updated' && <p>Campaign settings updated</p>}
                        {log.action === 'deleted' && <p>Campaign deleted</p>}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
